import type { DeclaredComplementRelationship } from '../../../../types/complementInvite';

export const COMPLEMENT_DISCLOSURE = 'El complemento de renta no es una aprobación. '
  + 'La decisión depende de la institución que financia el crédito hipotecario '
  + 'y será revisada por ella.';

export const CREDIT_RESPONSIBILITY_NOTICE = 'Se evaluarán tus condiciones para formar parte '
  + 'de un crédito hipotecario junto a quien te invitó.';

export const LEGAL_COPY_IS_PROVISIONAL = true;

export const OFFER_TITLE = '¿Sumas los ingresos de un familiar?';

export const OFFER_BODY_APPROVED = 'Aumenta el monto de tu crédito invitándole a evaluarse con nosotros';

export const OFFER_BODY_REJECTED = 'Invítale a evaluarse con nosotros para sumar sus ingresos a tu solicitud';

export const offerBody = (leadApproved: boolean) => (
  leadApproved ? OFFER_BODY_APPROVED : OFFER_BODY_REJECTED
);

export const OFFER_CTA = 'Complementar mi renta';

export const CLAVE_UNICA_NOTICE = 'La persona que invites deberá ingresar con su ClaveÚnica para '
  + 'que podamos verificar sus ingresos.';

export const INVITEE_CLAVE_UNICA_NOTICE = 'Para continuar deberás ingresar con tu ClaveÚnica, '
  + 'así podemos verificar tus ingresos.';

export const PICKER_TITLE = '¿Con quién quieres complementar?';

export const OTHER_COMPLETED_TITLE = 'Ya fueron evaluados';

export const SHARE_TITLE = 'Comparte el enlace';

export const SHARE_BODY = 'Envíale este enlace a la persona que va a complementar tu renta. '
  + 'Al abrirlo podrá completar sus datos por su cuenta.';

export const SHARE_TEXT = 'Te invito a complementar mi solicitud de crédito hipotecario en Banca.Me';

export const FALLBACK_LEAD_NAME = 'quien te invitó';

export const titleCaseName = (leadFirstName?: string) => {
  if (!leadFirstName || !leadFirstName.trim()) return FALLBACK_LEAD_NAME;
  return leadFirstName
    .toLowerCase()
    .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
};

export const invitedByLine = (leadFirstName: string) => (
  `${titleCaseName(leadFirstName)} te invita a complementar ingresos`
);

export const INVITE_DEAD_TITLE = 'Este enlace ya no está disponible';

export const INVITE_DEAD_BODY = 'La solicitud a la que te invitaron ya no admite un complemento de renta. '
  + 'Si crees que es un error, contacta a la persona que te compartió el enlace.';

export const INVITE_RATE_LIMIT_TITLE = 'Demasiados intentos';

export const INVITE_RATE_LIMIT_BODY = 'Espera un momento e intenta abrir el enlace nuevamente.';

export type InviteeResultBodyParagraph = string
| { bold: string, rest: string }
| { before: string, bold: string, after?: string }
| { italicGray: string };

export const inviteeResultCopy = (params: {
  leadComplementAccepted?: boolean,
  ownPreApproved: boolean,
  leadFirstName?: string,
  alreadyEvaluated?: boolean,
  partnerName?: string,
}): { title: string, body: InviteeResultBodyParagraph[] } => {
  const nombre = titleCaseName(params.leadFirstName);
  const partnerName = params.partnerName?.trim() || 'la inmobiliaria';
  const jointRejectedTitle = `La solicitud de ${nombre} no fue aprobada`;
  const jointAcceptedTitle = `${nombre} quedó pre-aprobado con tus ingresos`;
  const finalDecisionBody = 'La decisión final la toma la institución que financia el crédito hipotecario.';
  const jointRejectedBody = `Evaluamos tus ingresos junto con los de ${nombre}. `
    + 'La institución que financia el crédito no aprobó esa solicitud.';

  if (params.alreadyEvaluated) {
    return {
      title: `No pudimos complementar la solicitud de ${nombre}`,
      body: [
        {
          italicGray: 'Tu evaluación crediticia ya estaba lista, así que no volvimos a evaluarte como complemento.',
        },
        {
          before: `Para sumar tus ingresos a la solicitud de ${nombre}, debes `,
          bold: `hablar con ${partnerName}`,
          after: ' para complementar de forma manual.',
        },
      ],
    };
  }

  if (params.leadComplementAccepted && params.ownPreApproved) {
    return {
      title: jointAcceptedTitle,
      body: [
        { italicGray: finalDecisionBody },
        {
          bold: 'Además, evaluamos tus ingresos por separado:',
          rest: ' lo que verás cerrando esta notificación es tu propia pre-aprobación.',
        },
      ],
    };
  }
  if (params.leadComplementAccepted && !params.ownPreApproved) {
    return {
      title: jointAcceptedTitle,
      body: [
        { italicGray: finalDecisionBody },
        {
          before: 'Con tus ingresos por separado todavía ',
          bold: 'no podemos pre-aprobarte un hipotecario propio.',
          after: ' Puedes volver a evaluarte cuando cambien tus ingresos.',
        },
      ],
    };
  }
  if (!params.leadComplementAccepted && params.ownPreApproved) {
    return {
      title: jointRejectedTitle,
      body: [
        { italicGray: 'La institución que financia el crédito no aprobó la solicitud conjunta.' },
        {
          bold: 'Tu evaluación individual sí quedó pre-aprobada:',
          rest: ' es la que verás cerrando esta notificación.',
        },
      ],
    };
  }
  return {
    title: jointRejectedTitle,
    body: [
      { italicGray: jointRejectedBody },
      {
        before: 'Lo que verás cerrando esta notificación será el resultado de ',
        bold: 'tu evaluación para este producto.',
      },
    ],
  };
};

export const mintErrorCopy = (message: unknown) => {
  switch (message) {
    case 'ERR_PLR_NOT_COMPLEMENTABLE':
      return 'Esta solicitud ya no admite un complemento de renta.';
    case 'ERR_INVALID_DECLARED_RELATIONSHIP':
      return 'El vínculo seleccionado no es válido.';
    case 'ERR_CANNOT_COMPLEMENT_SELF':
      return 'No puedes complementar tu propia solicitud.';
    default:
      return 'No pudimos generar el enlace. Intenta nuevamente en unos minutos.';
  }
};

export const RELATIONSHIP_LABELS: Record<DeclaredComplementRelationship, string> = {
  conyuge: 'Cónyuge / Unión civil',
  hijos_en_comun: 'Hijos en común',
  hijo: 'Hijo/a',
  padre: 'Padre/madre',
  hermano: 'Hermano/a',
  otro: 'Otra persona',
};
